/**
 * Boli Live - Wallet Page
 *
 * Wallet dashboard with balance, top-up, and transaction history.
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Wallet,
  Plus,
  ArrowDownLeft,
  ArrowUpRight,
  Clock,
  ChevronLeft,
  RefreshCw,
  Lock,
} from 'lucide-react';
import { useAuthStore } from '../lib/stores/auth-store';
import { TopUpModal } from '../components/TopUpModal';
import { formatPKR, cn } from '../lib/utils';
import { API_BASE_URL } from '../lib/api-config';

interface WalletData {
  id: string;
  balance: number;
  lockedBalance: number;
  currency: string;
  isActive: boolean;
}

interface Transaction {
  id: string;
  type: 'deposit' | 'withdrawal' | 'payment' | 'refund';
  amount: number;
  provider?: string;
  status: 'pending' | 'completed' | 'failed';
  reference: string;
  description: string;
  created: string;
}

export default function WalletPage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [wallet, setWallet] = useState<WalletData | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchWallet = async () => {
    try {
      const token = localStorage.getItem('boli_auth_token');
      const response = await fetch(`${API_BASE_URL}/api/wallet/balance`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setWallet(data.wallet);
      }
    } catch (error) {
      console.error('Failed to fetch wallet:', error);
    }
  };

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('boli_auth_token');
      const response = await fetch(`${API_BASE_URL}/api/wallet/transactions`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
      }
    } catch (error) {
      console.error('Failed to fetch transactions:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchWallet(), fetchTransactions()]);
    setIsRefreshing(false);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/profile');
      return;
    }

    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([fetchWallet(), fetchTransactions()]);
      setIsLoading(false);
    };

    loadData();
  }, [isAuthenticated, navigate]);

  const handleTopUpSuccess = async () => {
    await handleRefresh();
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownLeft className="w-5 h-5 text-green-500" />;
      case 'payment':
        return <ArrowUpRight className="w-5 h-5 text-red-500" />;
      case 'refund':
        return <ArrowDownLeft className="w-5 h-5 text-blue-500" />;
      default:
        return <Clock className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'deposit':
      case 'refund':
        return 'text-green-600';
      case 'payment':
      case 'withdrawal':
        return 'text-red-600';
      default:
        return 'text-foreground';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 bg-muted rounded-full mb-4" />
          <div className="h-4 w-32 bg-muted rounded" />
        </div>
      </div>
    );
  }

  const availableBalance = (wallet?.balance || 0) - (wallet?.lockedBalance || 0);

  return (
    <div className="min-h-screen pb-20 bg-background">
      {/* Header */}
      <div className="bg-primary text-white">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <h1 className="font-bold text-lg">My Wallet</h1>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 rounded-full hover:bg-white/10 transition-colors"
          >
            <RefreshCw className={cn("w-5 h-5", isRefreshing && "animate-spin")} />
          </button>
        </div>

        {/* Balance Card */}
        <div className="px-4 pb-8 pt-4">
          <motion.div
            className="bg-white/10 backdrop-blur rounded-2xl p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Wallet className="w-6 h-6" />
              </div>
              <div>
                <p className="text-white/70 text-sm">Available Balance</p>
                <p className="text-3xl font-bold font-display">{formatPKR(availableBalance)}</p>
              </div>
            </div>

            {wallet && (wallet.lockedBalance ?? 0) > 0 && (
              <div className="flex items-center gap-2 text-sm text-white/70 bg-white/10 rounded-lg px-3 py-2">
                <Lock className="w-4 h-4" />
                <span>Locked for bidding: {formatPKR(wallet.lockedBalance ?? 0)}</span>
              </div>
            )}

            {/* Top Up Button */}
            <motion.button
              onClick={() => setIsTopUpOpen(true)}
              className="w-full mt-4 py-4 rounded-xl bg-white text-primary font-semibold flex items-center justify-center gap-2 hover:bg-white/90 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Plus className="w-5 h-5" />
              Top Up Wallet
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="px-4 -mt-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-muted-foreground mb-1">Total Balance</p>
            <p className="text-lg font-bold text-foreground font-display">
              {formatPKR(wallet?.balance || 0)}
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4 shadow-sm">
            <p className="text-xs text-muted-foreground mb-1">Locked</p>
            <p className="text-lg font-bold text-foreground font-display">
              {formatPKR(wallet?.lockedBalance || 0)}
            </p>
          </div>
        </div>
      </div>

      {/* Transaction History */}
      <div className="px-4 mt-6">
        <h2 className="font-bold text-foreground mb-4">Transaction History</h2>

        {transactions.length === 0 ? (
          <div className="text-center py-12 bg-card border border-border rounded-2xl">
            <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-foreground font-medium">No transactions yet</p>
            <p className="text-sm text-muted-foreground">
              Top up your wallet to get started
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {transactions.map((tx) => (
              <motion.div
                key={tx.id}
                className="bg-card border border-border rounded-xl p-4 shadow-sm"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center">
                    {getTransactionIcon(tx.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-foreground capitalize truncate">
                      {tx.type}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {tx.description || tx.reference}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={cn('font-bold font-display', getTransactionColor(tx.type))}>
                      {tx.type === 'deposit' || tx.type === 'refund' ? '+' : '-'}
                      {formatPKR(tx.amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(tx.created)}
                    </p>
                  </div>
                </div>
                {tx.status === 'pending' && (
                  <div className="mt-2 text-xs text-yellow-600 bg-yellow-50 rounded-lg px-3 py-1 inline-block">
                    Pending
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Top Up Modal */}
      <TopUpModal
        isOpen={isTopUpOpen}
        onClose={() => setIsTopUpOpen(false)}
        onSuccess={handleTopUpSuccess}
        currentBalance={availableBalance}
      />
    </div>
  );
}
